import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

const inputCls = "w-full border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none transition-colors focus:border-accent";

const EMPTY = {
  project_id: "", site_id: "", schedule_id: "", work_type: "土木", title: "", description: "",
  procedure: "", scope: "", quantity: "", unit: "", required_count: 0, required_skills: "",
  tools: "", machines: "", materials: "", cautions: "", manager_id: "", status: "未着手", due_date: "",
};

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

const STATUS_COLORS = { 未着手: "text-gray-400", 作業中: "text-accent", 完了: "text-green-400", 保留: "text-amber-400" };

export default function AdminInstructions() {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [sites, setSites] = useState([]);
  const [staff, setStaff] = useState([]);
  const [workTypes, setWorkTypes] = useState(["土木", "草刈り", "伐採", "太陽光設備工事", "補修", "その他"]);
  const [filterProject, setFilterProject] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/work-instructions", { params: filterProject ? { project_id: filterProject } : {} }).then((r) => setItems(r.data)).catch(() => toast.error("取得に失敗しました"));
  };

  useEffect(() => {
    api.get("/admin/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/admin/sites").then((r) => setSites(r.data)).catch(() => {});
    api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {});
    api.get("/admin/work-types").then((r) => setWorkTypes(r.data.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterProject]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/work-instructions/${editing.id}`, form);
      else await api.post("/admin/work-instructions", form);
      toast.success("保存しました");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`「${s.title}」を削除しますか？`)) return;
    try {
      await api.delete(`/admin/work-instructions/${s.id}`);
      toast.success("削除しました");
      load();
    } catch {
      toast.error("削除に失敗しました");
    }
  };

  const projectName = (id) => projects.find((p) => p.id === id)?.name || "—";
  const siteName = (id) => sites.find((s) => s.id === id)?.name || "";
  const formSites = sites.filter((s) => !form.project_id || s.project_id === form.project_id);

  return (
    <div data-testid="admin-instructions-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-mincho text-2xl font-bold text-white">作業指示</h1>
          <p className="mt-1 font-mono text-[10px] tracking-[0.3em] text-gray-500">WORK INSTRUCTIONS — 現場への作業内容・手順・注意事項</p>
        </div>
        <div className="flex items-center gap-3">
          <select data-testid="instructions-filter-project" value={filterProject} onChange={(e) => setFilterProject(e.target.value)} className="border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none focus:border-accent">
            <option value="">すべての案件</option>
            {projects.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
          </select>
          <button data-testid="instruction-add-button" onClick={() => { setForm({ ...EMPTY, project_id: filterProject, work_type: workTypes[0] || "土木" }); setEditing({}); }} className="flex items-center gap-2 bg-accent px-5 py-2.5 font-display text-xs font-bold tracking-[0.15em] text-ink transition-colors hover:bg-accent-hover">
            <Plus className="h-4 w-4" />
            指示を登録
          </button>
        </div>
      </div>

      <div className="mt-8 overflow-x-auto border border-line">
        <table className="w-full min-w-[800px] text-left text-sm">
          <thead>
            <tr className="border-b border-line bg-coal font-mono text-[10px] tracking-[0.2em] text-gray-500">
              <th className="px-4 py-3">指示</th>
              <th className="px-4 py-3">工種</th>
              <th className="px-4 py-3">案件 / 現場</th>
              <th className="px-4 py-3">期限</th>
              <th className="px-4 py-3">状態</th>
              <th className="px-4 py-3">操作</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} data-testid={`instruction-row-${s.id}`} className="border-b border-line bg-panel last:border-b-0">
                <td className="px-4 py-3">
                  <p className="text-xs font-bold text-white">{s.title}</p>
                  {s.description && <p className="mt-0.5 max-w-xs truncate text-[10px] text-gray-500">{s.description}</p>}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-400">{s.work_type}</td>
                <td className="px-4 py-3 text-xs text-gray-400">{projectName(s.project_id)}{siteName(s.site_id) ? ` / ${siteName(s.site_id)}` : ""}</td>
                <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-gray-400">{s.due_date || "—"}</td>
                <td className={`whitespace-nowrap px-4 py-3 text-xs ${STATUS_COLORS[s.status] || "text-gray-400"}`}>{s.status}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button data-testid={`instruction-edit-${s.id}`} onClick={() => { setForm({ ...EMPTY, ...s }); setEditing(s); }} className="border border-line p-2 text-gray-400 transition-colors hover:border-accent hover:text-accent">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button data-testid={`instruction-delete-${s.id}`} onClick={() => remove(s)} className="border border-line p-2 text-gray-400 transition-colors hover:border-red-500 hover:text-red-400">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">作業指示が登録されていません。</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm">
          <form data-testid="instruction-form" onSubmit={save} className="max-h-[90vh] w-full max-w-2xl space-y-4 overflow-y-auto border border-line bg-coal p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-mincho text-lg font-bold text-white">{editing.id ? "作業指示を編集" : "作業指示を登録"}</h2>
              <button type="button" data-testid="instruction-form-close" onClick={() => setEditing(null)} className="text-gray-500 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="工事案件 *">
                <select data-testid="instruction-form-project" required value={form.project_id} onChange={(e) => setForm((f) => ({ ...f, project_id: e.target.value, site_id: "" }))} className={inputCls}>
                  <option value="">選択してください</option>
                  {projects.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
                </select>
              </Field>
              <Field label="現場">
                <select data-testid="instruction-form-site" value={form.site_id} onChange={set("site_id")} className={inputCls}>
                  <option value="">—</option>
                  {formSites.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                </select>
              </Field>
              <Field label="工種">
                <select data-testid="instruction-form-work-type" value={form.work_type} onChange={set("work_type")} className={inputCls}>
                  {workTypes.map((w) => (<option key={w}>{w}</option>))}
                </select>
              </Field>
              <Field label="指示の件名 *"><input data-testid="instruction-form-title" required value={form.title} onChange={set("title")} className={inputCls} placeholder="掘削作業" /></Field>
              <Field label="数量"><input data-testid="instruction-form-quantity" value={form.quantity} onChange={set("quantity")} className={inputCls} placeholder="10" /></Field>
              <Field label="単位"><input data-testid="instruction-form-unit" value={form.unit} onChange={set("unit")} className={inputCls} placeholder="m²" /></Field>
              <Field label="期限"><input data-testid="instruction-form-due" type="date" value={form.due_date} onChange={set("due_date")} className={inputCls} /></Field>
              <Field label="責任者">
                <select data-testid="instruction-form-manager" value={form.manager_id} onChange={set("manager_id")} className={inputCls}>
                  <option value="">—</option>
                  {staff.filter((s) => s.is_active).map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                </select>
              </Field>
              <Field label="状態">
                <select data-testid="instruction-form-status" value={form.status} onChange={set("status")} className={inputCls}>
                  <option>未着手</option><option>作業中</option><option>完了</option><option>保留</option>
                </select>
              </Field>
            </div>
            <Field label="作業内容"><textarea data-testid="instruction-form-description" rows={3} value={form.description} onChange={set("description")} className={`resize-y ${inputCls}`} /></Field>
            <Field label="作業手順"><textarea data-testid="instruction-form-procedure" rows={3} value={form.procedure} onChange={set("procedure")} className={`resize-y ${inputCls}`} /></Field>
            <Field label="工具・持ち物"><input data-testid="instruction-form-tools" value={form.tools} onChange={set("tools")} className={inputCls} /></Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="機械"><input data-testid="instruction-form-machines" value={form.machines} onChange={set("machines")} className={inputCls} /></Field>
              <Field label="資材"><input data-testid="instruction-form-materials" value={form.materials} onChange={set("materials")} className={inputCls} /></Field>
            </div>
            <Field label="注意事項・安全"><textarea data-testid="instruction-form-cautions" rows={2} value={form.cautions} onChange={set("cautions")} className={`resize-y ${inputCls}`} /></Field>
            <button data-testid="instruction-form-submit" type="submit" disabled={saving} className="w-full bg-accent py-3 font-display text-sm font-bold tracking-[0.2em] text-ink transition-colors hover:bg-accent-hover disabled:opacity-50">
              {saving ? "保存中..." : "保存する"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
